import { KeyboardEvent, memo } from 'react';
import { css } from '@emotion/react';
import { Handle, Position, NodeProps } from 'reactflow';
import { color, Icon, spacing, typography } from '@uniquegood/realworld-studio-design';

import type { QuestNodeData } from '../types';
import { QUEST_NODE_WIDTH } from '../constants';

function QuestNode({ data }: NodeProps<QuestNodeData>) {
  const { quest, missionId, isSelected, onSelectQuest } = data;

  const handleSelectQuest = () => {
    onSelectQuest(missionId, quest.id);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      handleSelectQuest();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      css={[Container, isSelected && ContainerSelected]}
      onKeyDown={handleKeyDown}
      onClick={(e) => {
        e.stopPropagation();
        handleSelectQuest();
      }}
    >
      <Handle type="target" position={Position.Left} css={HandleStyle} />
      <span css={QuestName}>{quest.name}</span>
      <div css={Icons}>
        {!quest.isVisibleDefault && (
          <Icon icon="eye_slash_solid" size="11px" color={color.icon_disabled} />
        )}
        {quest.isLockedDefault && (
          <Icon icon="lock_solid" size="11px" color={color.icon_disabled} />
        )}
      </div>
      <Handle type="source" position={Position.Right} css={HandleStyle} />
    </div>
  );
}

export default memo(QuestNode);

const Container = css`
  width: ${QUEST_NODE_WIDTH}px;
  height: 44px;
  display: flex;
  align-items: center;
  padding: 0 ${spacing.common.small};
  border-radius: ${spacing.borderRadius.medium};
  border: 1.5px solid ${color.border_default_subdued};
  background: ${color.surface_default_default};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: ${color.border_primary_default};
  }
`;

const ContainerSelected = css`
  border-color: ${color.action_primary_default};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1), 0 0 0 2px ${color.action_primary_default}33;
`;

const HandleStyle = css`
  && {
    width: 8px;
    height: 8px;
    background-color: ${color.action_primary_default};
    border: 2px solid ${color.white};
  }
`;

const QuestName = css`
  flex: 1;
  font-size: ${typography.size.s2};
  color: ${color.text_default};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: ${spacing.common.xsmall};
`;

const Icons = css`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;
